import Cgl from 'Cgl';

import BaseConfig from './BaseConfig';
import ControlConfig from './ControlConfig';
import { isOverlappingRow, rowsSum } from '../utils';

const IsEmptyObject = Cgl.Object.IsEmptyObject;
const Clone = Cgl.Object.Clone;

/**
 * @private
 * @author chen yu
 * @date 07/01/2021 01:01:44
 * @class
 * @extends {BaseConfig}
 */
class ContainerConfig extends BaseConfig {

    constructor(options) {

        super(options);

        this.Type = options.Type;

        this.Children = [];

    }

    Export() {
        return {
            ...super.Export(),
            Children: this.Children.map(child => child.Export())
        };
    }

    DeleteRow(rowNumber) {

        const _children = this.Children.filter(child => {
            const _positionInfo = child.GetAbsolutePositionInfo();
            return isOverlappingRow(_positionInfo, { Row: rowNumber, Rows: 1 });
        });

        // 当前行有控件，不能删除
        if (_children.filter(child => child instanceof ControlConfig).length) {

            return false;

        }

        if (!_children.length) {

            this.Children.filter(child => child.GetAbsolutePositionInfo().Row >= rowNumber).forEach(child => {
                const _positionInfo = child.GetPositionInfo();
                _positionInfo.Row--; 
            });

            this.PositionInfo.Rows--;

        }

    }

    InsertRow(rowNumber) {

        const _children = this.Children.filter(child => {
            const _positionInfo = child.GetAbsolutePositionInfo();
            return _positionInfo.Row < rowNumber && (_positionInfo.Row + _positionInfo.Rows) > rowNumber
        });

        // 有占多含的控件，不能插入行
        if (_children.filter(child => child instanceof ControlConfig).length) {

            return false;

        }

        // 控件配置的行加一
        if (!_children.length) {

            this.Children.filter(child => child.GetAbsolutePositionInfo().Row >= rowNumber).forEach(child => {
                const _positionInfo = child.GetPositionInfo();
                _positionInfo.Row ++; 
            });

            this.PositionInfo.Rows++;
        }


        // _children.filter(child => child instanceof ContainerConfig).forEach(child => child.InsertRow(rowNumber));

    }

    GetEmptyRowNumberList() {

        const _positionInfo = this.GetPositionInfo();
        
        const _childPositionInfoList = this.Children.filter(child => child.ColInfo.ContorlCfg?.IsShow !== '0').map(child => child.GetPositionInfo());

        const _rowList = new Array(_positionInfo.Rows).fill(0).map((v, i) => i + 1);

        return _rowList.filter(rowNumber => {
            return !_childPositionInfoList.filter(childPositionInfo => {
                return childPositionInfo.Row<= rowNumber && (childPositionInfo.Row + childPositionInfo.Rows) > rowNumber;
            }).length;
        })

    }

    GetChildrenPositionInfo() {

        if (!this.Children.length) {
            return {
                Column: 1,
                Columns: 0,
                Row: 1,
                Rows: 0
            };
        }

        // 没有位置信息的节点
        const _emptyPositionChildren = [];

        let _minRowNumber = Infinity;
        let _maxRowNumber = -Infinity;
        let _minColNumber = Infinity;
        let _maxColNumber = -Infinity;

        this.Children.forEach(child => {

            const _positionInfo = child.GetPositionInfo();

            if (IsEmptyObject(_positionInfo)) {
                _emptyPositionChildren.push(_positionInfo);
                return;
            }

            _minRowNumber = Math.min(_minRowNumber, _positionInfo.Row);
            _maxRowNumber = Math.max(_maxRowNumber, _positionInfo.Row + _positionInfo.Rows);

            _minColNumber = Math.min(_minColNumber, _positionInfo.Column);
            _maxColNumber = Math.max(_maxColNumber, _positionInfo.Column + _positionInfo.Columns);

        });

        return {
            Column: _minColNumber,
            Row: _minRowNumber,
            Rows: _maxRowNumber - _minRowNumber + _emptyPositionChildren.length,
            Columns: _maxColNumber - _minColNumber
        };
    }

    GetLayoutInfo() {

        const _columnNumber = this.Root.ColumnNumber;

        return {
            PositionInfo: this.GetPositionInfo(),
            Children: this.Children.map(child => child.GetLayoutInfo())
                .sort((child1LayoutInfo, cihld2LayoutInfo) => {

                    const _isSameRow = (cihld2LayoutInfo.Row + cihld2LayoutInfo.Rows)
                        - (child1LayoutInfo.Row + cihld2LayoutInfo.Rows);

                    if (_isSameRow !== 0) {
                        return _isSameRow;
                    }

                    return (cihld2LayoutInfo.Column - cihld2LayoutInfo.Columns)
                        - (child1LayoutInfo.Column - child1LayoutInfo.Columns);

                })
        };
    }

    /**
     * @method
     * @param {object} options 
     * @returns {void}
     * @description 追加新节点到当前容器中
     */
    Append(options) {


        if (options.ColInfo && this.Root.ColInfoList.includes(options.ColInfo)) {
            this.Root.ColInfoList.push(options.ColInfo);
        }

        const _positionInfo = this.GetPositionInfo();
        let _childrenPositionInfo = this.GetChildrenPositionInfo();

        options = {
            ...options,
            PositionInfo: Object.assign({
                Column: 1,
                Row: _childrenPositionInfo.Row + _childrenPositionInfo.Rows,
                Columns: this.Root.ColumnNumber,
                Rows: 1
            }, options.PositionInfo || {})
        };

        const _child = new (Array.isArray(options.Children) ? ContainerConfig : ControlConfig)({
            ...options,
            parent: this,
            root: this.Root,
            ColInfo: typeof options.Name === 'string'
                ? options.ColInfo || this.Root.ColInfoList.find(item => item.ColName === options.Name)
                : null
        });

        if (_child instanceof ContainerConfig) {
            (options.Children || []).forEach(child => {
                _child.Append(child);
            });
        }

        this.Children.push(_child);


        _childrenPositionInfo = this.GetChildrenPositionInfo();

        if (this instanceof ContainerConfig) {
            if (_positionInfo.Rows < _childrenPositionInfo.Row + _childrenPositionInfo.Rows - 1) {
                _positionInfo.Rows += _childrenPositionInfo.Row + _childrenPositionInfo.Rows - 1 - _positionInfo.Rows;
            }

            if (_positionInfo.Columns < _childrenPositionInfo.Column + _childrenPositionInfo.Columns - 1) {
                _positionInfo.Columns += _childrenPositionInfo.Column + _childrenPositionInfo.Columns - 1 - _positionInfo.Columns;
            }
        }

    }

    /**
     * @method
     * @param {ContainerConfig | ControlConfig} child 需要移除的子节点
     * @returns {void}
     * @description 移除子节点
     */
    RemoveChild(child) {

        const _index = this.Children.findIndex(c => c === child);

        if (_index === -1) {
            return;
        }

        const _child = this.Children.splice(_index, 1)[0];
        _child.Parent = null;

    }

    GetRowsInfo() {

        const _positionInfo = this.GetPositionInfo();

        if (!this.VueComponent || _positionInfo.Rows < 1) {
            return {
                Row: 1,
                RowsHeight: []
            };
        }

        let _hasContainerChild = this.Children.some(child => child instanceof ContainerConfig);

        if (!_hasContainerChild) {

            return {
                Row: _positionInfo.Row,
                RowsHeight: window.getComputedStyle(this.VueComponent.$el)
                    .gridTemplateRows.replaceAll("40px", "auto").split(" ")
            };

        }

        const _childContainerRowInfo = this.Children
            .filter(child => child instanceof ContainerConfig)
            .map(child => child.GetRowsInfo());

        const _rowsHeight = [];

        for (let _row = 1, _rows = _positionInfo.Rows; _row <= _rows; _row++) {

            let _index = _childContainerRowInfo.findIndex(
                childRowsInfo => childRowsInfo.Row <= _row && _row < childRowsInfo.Row + childRowsInfo.RowsHeight.length
            );

            if (_index === -1) {
                _rowsHeight.push("auto");
            } else {
                _rowsHeight.push(..._childContainerRowInfo[_index].RowsHeight);

                _row = _row + _childContainerRowInfo[_index].RowsHeight.length - 1;
            }

        }

        this.VueComponent.$el.style.gridTemplateRows = _rowsHeight.join(" ");

        return {
            Row: _positionInfo.Row,
            RowsHeight: _rowsHeight
        };

    }

    /**
     * @method
     * @author chen yu
     * @date 29/01/2021 20:01:49
     * @memberof ContainerConfig
     * @returns {Array}
     * @description 将子节点分类成多个从上向下排列的块
     */
    GetSectionList() {
        
        const _positionInfo = this.GetPositionInfo();

        const _children = this.Children;

        
        const _secionList = [];

        _children.forEach(child => {

            const _childPositionInfo = child.GetPositionInfo();

            const _overlapSection = _secionList.find(section => isOverlappingRow(section.PositionInfo, _childPositionInfo));

            if (_overlapSection) {
                
                _overlapSection.Children.push(child);

                _overlapSection.Rows = rowsSum(_overlapSection.PositionInfo, child.PositionInfo);

            } else {
                _secionList.push({
                    PositionInfo: {
                        Row: _childPositionInfo.Row,
                        Rows: _childPositionInfo.Rows
                    },
                    Children: [child]
                })
            }

        });

        _secionList.sort((a, b) => a.PositionInfo.Row - b.PositionInfo.Row);


        return _secionList; 

    }

}

export default ContainerConfig;